import React, { useEffect, useState, useCallback } from 'react';
import TrackingMap from './TrackingMap';
import { titleCase, upperCase, formatDateOnly, formatDateTime } from '../../../lib/utils';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { TrackingDataFTL } from '../../../types/tracking';
import OrderDetails from './OrderDetails';
import ShareLinkModal from 'components/ui/share-link-modal';
import { Button } from 'components/ui/button';
import { generateShareLink } from 'features/shipments/api';
import { useMediaQuery } from '../../../hooks/use-mobile';

const TRUCK_W = 70; // px
const TRUCK_H = 48; // px
const GAP_PX = 0;

interface CustomProgressBarProps {
    progress: number;
}

const CustomProgressBar: React.FC<CustomProgressBarProps> = ({ progress }) => {
    const pct = Math.max(0, Math.min(progress, 100));
    const truckLeft = `min(${pct}%, calc(100% - ${TRUCK_W / 2}px))`;

    return (
        <div className="relative flex items-center justify-between mb-2 px-1">
            <div className="w-3 h-3 bg-green-500 rounded-full z-10 flex-shrink-0" />

            <div className="flex-1 mx-3 relative flex items-center overflow-visible">
                {pct > 0 && (
                    <div
                        className="h-1 bg-blue-500 absolute left-0 top-1/2 -translate-y-1/2 z-10"
                        style={{ width: `max(0px, calc(${pct}% - ${GAP_PX / 2 + TRUCK_W / 2}px))` }}
                    />
                )}

                <div
                    className="h-0 border-t-2 border-dotted border-gray-300 absolute top-1/2 -translate-y-1/2 z-0"
                    style={{
                        left: `calc(${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)`,
                        width: `max(0px, calc(100% - (${pct}% + ${GAP_PX / 2 + TRUCK_W / 2}px)))`,
                        borderTopWidth: '2px',
                    }}
                />

                <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 pointer-events-none"
                    style={{ left: truckLeft, width: TRUCK_W, height: TRUCK_H }}
                >
                    <img
                        src="/images/truck.png"
                        alt="Truck"
                        className="w-full h-full object-contain select-none"
                        draggable={false}
                    />
                </div>
            </div>
            <div className="w-3 h-3 bg-gray-300 rounded-full z-10 flex-shrink-0" />
        </div>
    );
};

interface TrackingPageFTLProps {
    data: TrackingDataFTL;
    refreshData: () => void;
    isPublic?: boolean;
    token: string;
}

const TrackingPageFTL: React.FC<TrackingPageFTLProps> = ({ data, refreshData, isPublic, token }) => {
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const { setRightHeaderActions } = useBreadcrumbs();

    const cnNumber = data.cn_number;
    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const [shareUrl, setShareUrl] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    // Derived state
    const progressPct = data ? Math.round((data.progress.covered_distance / data.progress.total_distance) * 100) : 0;

    const handleShareClick = useCallback(async () => {
        if (!cnNumber) return;
        try {
            const response = await generateShareLink(cnNumber);
            const fullUrl = `${window.location.origin}/shipments/track/public?token=${response.share_token}`;
            setShareUrl(fullUrl);
            setIsShareModalOpen(true);
        } catch (err) {
            setError("Failed to generate share link.");
        }
    }, [cnNumber]);

    useEffect(() => {
        if (!isPublic) {
            setRightHeaderActions(
                <div className="hidden lg:block">
                    <Button variant="outline" size="sm" onClick={handleShareClick} className="bg-white border-gray-200 text-xs px-3 h-8 shadow-sm hover:bg-gray-50">
                        <span className="mr-1.5">🔗</span> Share Link
                    </Button>
                </div>
            );
        }

        return () => {
            setRightHeaderActions(null);
        };
    }, [setRightHeaderActions, isPublic, handleShareClick]);

    const lastUpdatedFormatted = formatDateTime(data.last_updated);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(shareUrl);
        setIsShareModalOpen(false);
    };

    const isOnHold = data.status === 'on_hold';
    const isAwaitingPickup = data.status === 'awaiting_pickup';
    const stockDays = data.stock_since
        ? Math.floor((Date.now() - new Date(data.stock_since).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

    // Helper render functions to eliminate code duplication
    const renderPublicBanner = (isDesktopView: boolean) => isPublic ? (
        <div className={isDesktopView ? "mb-6 text-center md:text-left" : "mb-3.5 text-center"}>
            <img src="/logo.png" alt="Logo" className={isDesktopView ? "h-10 mb-3 mx-auto md:mx-0" : "h-6 sm:h-7 mb-1.5 mx-auto"} />
            <h2 className={isDesktopView ? "text-xl font-bold text-gray-800" : "text-sm sm:text-base font-bold text-gray-800 tracking-tight"}>Shipment Tracking</h2>
            <p className={isDesktopView ? "text-sm text-gray-500 mt-0.5" : "text-xs text-gray-500 mt-0.5"}>
                CN Number: <span className="font-semibold text-gray-700">{data.cn_number}</span>
            </p>
        </div>
    ) : null;

    const renderFromToGrid = (isDesktopView: boolean) => (
        <div className={isDesktopView ? "flex flex-row justify-between items-start gap-6 mb-8" : "grid grid-cols-2 gap-4 mb-5"}>
            <div className="text-left min-w-0 flex-1">
                <div className={isDesktopView ? "text-gray-600 text-sm" : "text-gray-500 text-[11px] uppercase tracking-wider font-semibold mb-0.5"}>From</div>
                <div className={isDesktopView ? "text-lg font-bold text-gray-900 mb-1 break-words" : "text-sm sm:text-base font-semibold text-gray-900 leading-snug break-words mb-1"}>
                    {upperCase(data.consignor)}
                </div>
                <div className={isDesktopView ? "text-md text-gray-700 mb-1" : "text-xs text-gray-600 mb-0.5"}>
                    left {titleCase(data.origin)}
                </div>
                <div className={isDesktopView ? "text-left" : ""}>
                    <div className={isDesktopView ? "text-sm font-semibold text-gray-900" : "text-xs font-medium text-gray-700"}>
                        {data.left_date ? formatDateOnly(data.left_date) : '-'}
                    </div>
                </div>
            </div>

            <div className={isDesktopView ? "text-right min-w-0 flex-1" : "text-right min-w-0"}>
                <div className={isDesktopView ? "text-gray-600 text-sm" : "text-gray-500 text-[11px] uppercase tracking-wider font-semibold mb-0.5"}>To</div>
                <div className={isDesktopView ? "text-lg font-bold text-gray-900 mb-1 break-words" : "text-sm sm:text-base font-semibold text-gray-900 leading-snug break-words mb-1"}>
                    {upperCase(data.consignee)}
                </div>
                <div className={isDesktopView ? "text-md text-gray-700 mb-1" : "text-xs text-gray-600 mb-0.5"}>
                    arriving at {titleCase(data.destination)}
                </div>
                <div className={isDesktopView ? "text-right" : ""}>
                    <div className={isDesktopView ? "text-sm font-semibold text-gray-900" : "text-xs font-medium text-gray-700"}>
                        {formatDateOnly(data.eta)}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProgressBarSection = (isDesktopView: boolean) => (
        <div className={isDesktopView ? "mb-8" : "mb-6"}>
            <CustomProgressBar progress={progressPct} />
            <div className={isDesktopView ? "flex justify-between text-sm text-gray-500 mt-2 px-1" : "flex justify-between text-xs text-gray-500 mt-2 px-1 font-medium"}>
                <span>{data.progress.covered_distance} km travelled</span>
                <span>{data.progress.total_distance} km total{isDesktopView ? " distance" : ""}</span>
                <span>{data.progress.remaining_distance} km remaining</span>
            </div>
        </div>
    );

    const renderStatusBox = (isDesktopView: boolean) => (
        <div className={`${isDesktopView ? "mt-8 p-4 rounded-lg border" : "p-4 rounded-xl border"} ${(isOnHold || isAwaitingPickup)
            ? (isDesktopView ? 'bg-orange-50 border-orange-200' : 'bg-orange-50/80 border-orange-200')
            : (isDesktopView ? 'bg-blue-50 border-blue-200' : 'bg-blue-50/80 border-blue-200')
            }`}>
            <div className="flex items-center gap-2">
                <div className={`rounded-full animate-pulse ${(isOnHold || isAwaitingPickup) ? 'bg-orange-500' : 'bg-blue-500'} ${isDesktopView ? 'w-2 h-2' : 'w-2.5 h-2.5 shrink-0'}`}></div>
                <span className={`text-sm font-medium ${(isOnHold || isAwaitingPickup) ? 'text-orange-900' : 'text-blue-900'}`}>
                    {isAwaitingPickup
                        ? `Awaiting Pickup at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                        : isOnHold
                        ? `Currently on hold at ${data.current_address} since ${data.stock_since ? formatDateOnly(data.stock_since) : '-'} (${stockDays} days)`
                        : `Currently in ${data.current_address}`
                    }
                </span>
            </div>
            <div className={`text-xs mt-1 ${isDesktopView ? "" : "pl-4"} ${(isOnHold || isAwaitingPickup) ? 'text-orange-700' : 'text-blue-700'}`}>
                Last updated: {lastUpdatedFormatted.date} {lastUpdatedFormatted.time}
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col relative overflow-hidden -mx-4 -mt-4 -mb-4 lg:m-0">
            {isDesktop ? (
                <div className="h-full flex flex-col overflow-hidden relative">
                    <div className="flex-1 min-h-0 flex flex-row gap-6 px-6 py-6 pb-6 overflow-hidden">
                        <div className="w-[50%] flex flex-col min-h-0">
                            {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">{error}</div>}
                            {renderPublicBanner(true)}
                            {renderFromToGrid(true)}
                            {renderProgressBarSection(true)}
                            {renderStatusBox(true)}
                            <div className="mt-6 flex-1 min-h-0">
                                <OrderDetails orderDetails={data.order_details} className="h-full flex flex-col" />
                            </div>
                        </div>
                        {data.cn_number && (
                            <div className="w-[50%] min-h-0 flex flex-col">
                                <TrackingMap cnNumber={data.cn_number} onDataRefresh={refreshData} isPublic={isPublic} token={token} />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="h-full flex flex-col overflow-y-auto relative bg-gray-50/50">
                    <div className="flex-1 min-h-0 flex flex-col gap-0 p-0 relative">
                        {data.cn_number && (
                            <div className="w-full h-[58vh] min-h-0 sticky top-0 z-0 flex flex-col shrink-0">
                                <TrackingMap cnNumber={data.cn_number} onDataRefresh={refreshData} isPublic={isPublic} token={token} onShare={handleShareClick} />
                            </div>
                        )}

                        <div className="w-full relative z-10 -mt-6 bg-white rounded-t-[28px] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] border-t border-gray-100 px-5 pt-3 pb-10 flex flex-col min-h-[55vh]">
                            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4 shrink-0" />
                            {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded">{error}</div>}
                            {renderPublicBanner(false)}
                            {renderFromToGrid(false)}
                            {renderProgressBarSection(false)}
                            {renderStatusBox(false)}
                            <div className="mt-6">
                                <OrderDetails orderDetails={data.order_details} className="flex flex-col" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ShareLinkModal
                isOpen={isShareModalOpen}
                shareUrl={shareUrl}
                onCopy={handleCopy}
                onClose={() => setIsShareModalOpen(false)}
            />
        </div>
    );
};

export default TrackingPageFTL;
